import React, { useState, useEffect, useCallback, useRef } from 'react';
import TextField from '@mui/material/TextField';
import Snackbar from '@mui/material/Snackbar';
import Autocomplete from '@mui/material/Autocomplete';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Divider from '@mui/material/Divider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { TEAM_ICON_DEFAULT } from '../../../common/staticData';
import CircularProgress from '@mui/material/CircularProgress';

import Search from './search';
import GameService from '../../../services/game.service';
import Upload from '../../../common/upload';
import PlayerFormDialog from '../PlayerTab/PlayerFormDialog';
import TeamFormDialog from '../TeamTab/TeamFormDialog';
import { getFormattedDate } from '../../newcoach/components/utilities';

const styles = {
    loader: {
        position: 'fixed',
        left: '0px',
        top: '0px',
        width: '100%',
        height: '100%',
        zIndex: 9999,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    }
};

const TIMER_POSITIONS = ['top-left', 'top-middle', 'top-right', 'bottom-left', 'bottom-right'];
const ENCODER_OPTIONS = ['Auto (best)', 'CPU (x264)', 'NVIDIA (NVENC)', 'Intel (QSV)', 'AMD (AMF)'];

const defaultTimerConfig = {
    enabled: false,
    fontName: 'Arial',
    fontSize: '25',
    fontColor: 'white',
    useBox: true,
    boxColor: '#000000',
    boxAlpha: 50,
    position: 'top-left',
    firstHalfStart: '00:00:00',
    secondHalfStart: '00:45:00',
    overtimeStart: '',
    downscale: false,
    encoder: 'Auto (best)'
};

const resolveBoxAlpha = (value) => {
    if (value === undefined || value === null) return defaultTimerConfig.boxAlpha;
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return defaultTimerConfig.boxAlpha;
    if (numeric <= 1 && numeric >= 0) {
        return Math.round(numeric * 100);
    }
    return Math.max(0, Math.min(100, Math.round(numeric)));
};
export default function GameFormDialog({ open, setOpen, gameListUpdated, actionType, editData, t }) {
    const descriptionElementRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alert, setAlert] = useState('');
    const [count, setCount] = useState(0);
    const [leagueOpen, setLeagueOpen] = useState(false);
    const [leagueName, setLeagueName] = useState('');
    const [teamOpen, setTeamOpen] = useState(false);

    const [gameDate, setGameDate] = useState(new Date());
    const [season, setSeason] = useState({});
    const [league, setLeague] = useState({});
    const [seasonList, setSeasonList] = useState([]);
    const [leagueList, setLeagueList] = useState([]);
    const [teamList, setTeamList] = useState([]);
    const [playerList, setPlayerList] = useState([]);

    const [positions, setPositions] = useState([]);
    const [roles, setRoles] = useState([]);
    const [ageGroups, setAgeGroups] = useState([]);

    const [homeTeam, setHomeTeam] = useState(false);
    const [awayTeam, setAwayTeam] = useState(false);
    const [playerOpen, setPlayerOpen] = useState(false);
    const [alertType, setAlertType] = useState('success');
    const [image, setImage] = useState('');

    const [videoUrl, setVideoUrl] = useState('');
    const [mobileVideoUrl, setMobileVideoUrl] = useState('');
    const [previousVideoUrl, setPreviousVideoUrl] = useState('');

    const [doneTagging, setDoneTagging] = useState(false);
    const [muteVideo, setMuteVideo] = useState(true);

    const [timerEnabled, setTimerEnabled] = useState(defaultTimerConfig.enabled);
    const [timerFontName, setTimerFontName] = useState(defaultTimerConfig.fontName);
    const [timerFontSize, setTimerFontSize] = useState(defaultTimerConfig.fontSize);
    const [timerFontColor, setTimerFontColor] = useState(defaultTimerConfig.fontColor);
    const [timerUseBox, setTimerUseBox] = useState(defaultTimerConfig.useBox);
    const [timerBoxColor, setTimerBoxColor] = useState(defaultTimerConfig.boxColor);
    const [timerBoxAlpha, setTimerBoxAlpha] = useState(defaultTimerConfig.boxAlpha);
    const [timerPosition, setTimerPosition] = useState(defaultTimerConfig.position);
    const [firstHalfStart, setFirstHalfStart] = useState(defaultTimerConfig.firstHalfStart);
    const [secondHalfStart, setSecondHalfStart] = useState(defaultTimerConfig.secondHalfStart);
    const [overtimeStart, setOvertimeStart] = useState(defaultTimerConfig.overtimeStart);
    const [timerDownscale, setTimerDownscale] = useState(defaultTimerConfig.downscale);
    const [timerEncoder, setTimerEncoder] = useState(defaultTimerConfig.encoder);
    const [timerLogos, setTimerLogos] = useState({ home: '', away: '' });
    const [timerMetadata, setTimerMetadata] = useState(null);
    const [timerMetadataWarning, setTimerMetadataWarning] = useState(null);
    const [timerStartTimesLoading, setTimerStartTimesLoading] = useState(false);
    const [timerStartTimesError, setTimerStartTimesError] = useState(null);
    const [hasFirstHalfStartTag, setHasFirstHalfStartTag] = useState(false);
    const [hasSecondHalfStartTag, setHasSecondHalfStartTag] = useState(false);

    const s3BaseUrl = process.env.REACT_APP_S3_URI || '';

    const stripS3Base = useCallback(
        (url) => {
            if (!url) return '';
            if (!s3BaseUrl) return url;
            return url.startsWith(s3BaseUrl) ? url.replace(s3BaseUrl, '') : url;
        },
        [s3BaseUrl]
    );


    const resetTimerSettings = useCallback(() => {
        setTimerFontName(defaultTimerConfig.fontName);
        setTimerFontSize(defaultTimerConfig.fontSize);
        setTimerFontColor(defaultTimerConfig.fontColor);
        setTimerUseBox(defaultTimerConfig.useBox);
        setTimerBoxColor(defaultTimerConfig.boxColor);
        setTimerBoxAlpha(defaultTimerConfig.boxAlpha);
        setTimerPosition(defaultTimerConfig.position);
        setFirstHalfStart(defaultTimerConfig.firstHalfStart);
        setSecondHalfStart(defaultTimerConfig.secondHalfStart);
        setOvertimeStart(defaultTimerConfig.overtimeStart);
        setTimerDownscale(defaultTimerConfig.downscale);
        setTimerEncoder(defaultTimerConfig.encoder);
        setTimerLogos({ home: '', away: '' });
        setTimerMetadata(null);
        setTimerMetadataWarning(null);
        setTimerStartTimesLoading(false);
        setTimerStartTimesError(null);
        setHasFirstHalfStartTag(false);
        setHasSecondHalfStartTag(false);
    }, []);

    const applyTimerConfig = useCallback(
        (config = {}) => {
            setTimerFontName(config.fontName ?? defaultTimerConfig.fontName);
            setTimerFontSize(String(config.fontSize ?? defaultTimerConfig.fontSize));
            setTimerFontColor(config.fontColor ?? defaultTimerConfig.fontColor);
            setTimerUseBox(config.useBox !== undefined ? Boolean(config.useBox) : defaultTimerConfig.useBox);
            setTimerBoxColor(config.boxColor ?? defaultTimerConfig.boxColor);
            setTimerBoxAlpha(resolveBoxAlpha(config.boxAlpha));
            setTimerPosition(config.position ?? defaultTimerConfig.position);
            setFirstHalfStart(config.firstHalfStart ?? defaultTimerConfig.firstHalfStart);
            setSecondHalfStart(config.secondHalfStart ?? defaultTimerConfig.secondHalfStart);
            setOvertimeStart(config.overtimeStart ?? defaultTimerConfig.overtimeStart);
            setTimerDownscale(Boolean(config.downscale));
            setTimerEncoder(config.encoder ?? defaultTimerConfig.encoder);
            setHasFirstHalfStartTag(Boolean(config.firstHalfStart));
            setHasSecondHalfStartTag(Boolean(config.secondHalfStart));
            const nextLogos = config.logos || null;
            setTimerLogos({
                home: typeof nextLogos?.home === 'string' ? nextLogos.home : nextLogos?.home?.image || '',
                away: typeof nextLogos?.away === 'string' ? nextLogos.away : nextLogos?.away?.image || ''
            });
            if (config.logos || config.goalDetails || config.goalSummary) {
                setTimerMetadata({
                    logos: config.logos ?? null,
                    goalDetails: config.goalDetails ?? null,
                    scoreSummary: config.goalSummary ?? null,
                });
            } else {
                setTimerMetadata(null);
            }
            setTimerMetadataWarning(null);
            setTimerStartTimesError(null);
            setTimerStartTimesLoading(false);
        },
        []
    );

    const fetchTimerMetadata = useCallback(async (gameId) => {
        if (!gameId) return;
        setTimerStartTimesLoading(true);
        setTimerStartTimesError(null);
        setTimerMetadataWarning(null);
        try {
            const metadata = await GameService.getGameTimerMetadata(gameId);

            const firstStart = metadata?.firstHalfStart ?? '';
            const secondStart = metadata?.secondHalfStart ?? '';
            const homeLogo = metadata?.logos?.home?.image ?? '';
            const awayLogo = metadata?.logos?.away?.image ?? '';

            setFirstHalfStart(firstStart);
            setSecondHalfStart(secondStart);
            setHasFirstHalfStartTag(Boolean(firstStart));
            setHasSecondHalfStartTag(Boolean(secondStart));
            setTimerLogos({ home: homeLogo, away: awayLogo });

            if (metadata && (metadata.logos || metadata.goalDetails || metadata.scoreSummary)) {
                setTimerMetadata({
                    logos: metadata.logos ?? null,
                    goalDetails: metadata.goalDetails ?? null,
                    scoreSummary: metadata.scoreSummary ?? null,
                });
            } else {
                setTimerMetadata(null);
            }

            if (metadata && metadata.taggingComplete === false) {
                setTimerMetadataWarning('Tagging is not complete yet. The timer will not queue until both halves are tagged.');
            }
        } catch (error) {
            console.error('Failed to load timer metadata', error);
            setTimerStartTimesError('Unable to load timer metadata.');
            setTimerMetadata(null);
            setTimerLogos({ home: '', away: '' });
            setHasFirstHalfStartTag(false);
            setHasSecondHalfStartTag(false);
        } finally {
            setTimerStartTimesLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            const { current: descriptionElement } = descriptionElementRef;
            if (descriptionElement !== null) {
                descriptionElement.focus();
            }
            const fetchDataPromises = [GameService.getAllPositions(), GameService.getAllRoles(), GameService.getAllAgeGroups()];

            Promise.all(fetchDataPromises)
                .then((results) => {
                    setPositions(results[0]);
                    setRoles(results[1]);
                    setAgeGroups(results[2]);
                })
                .catch((error) => {
                    console.error('An error occurred while fetching data:', error);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [open]);

    useEffect(() => {
        GameService.getAllSeasons().then((res) => {
            setSeasonList(res);
            setSeason(res[0]);
        });
        GameService.getAllLeagues().then((res) => {
            setLeagueList(res);
            setLeague(res[0]);
        });
    }, [count]);

    useEffect(() => {
        if (open && actionType === 'Add') {
            setTimerEnabled(defaultTimerConfig.enabled);
            resetTimerSettings();
        }
    }, [open, actionType, resetTimerSettings]);

    const handleDoneTagging = () => {
        if (!doneTagging)
            GameService.updatePlayersToplayersMinutesTable({ gameId: editData.id }).then((res) => {
            });
        setDoneTagging(!doneTagging);
    }

    useEffect(() => {
        if (actionType !== 'Edit' || !leagueList.length || !seasonList.length || !editData) return;
        // setSeason(seasonList.find(s => s.id === editData.season_id));
        const editSeason = seasonList.find((s) => s.id === editData.season_id);
        const editLeague = leagueList.find((s) => s.id === editData.league_id);
        editSeason && setSeason(editSeason);
        editLeague && setLeague(editLeague);
        setGameDate(editData.date);
        setVideoUrl(editData.video_url);
        setMobileVideoUrl(editData.mobile_video_url);
        setPreviousVideoUrl(editData.previous_video_url || '');
        setDoneTagging(editData.done_tagging);

        const hasTimer = Boolean(editData?.add_timer_id);
        setTimerEnabled(hasTimer);

        if (hasTimer) {
            let parsedConfig = editData.add_timer_config;
            if (parsedConfig && typeof parsedConfig === 'string') {
                try {
                    parsedConfig = JSON.parse(parsedConfig);
                } catch (error) {
                    parsedConfig = {};
                }
            }
            applyTimerConfig(parsedConfig || {});
        } else {
            resetTimerSettings();
        }
    }, [editData, seasonList, leagueList, actionType, applyTimerConfig, resetTimerSettings]);

    useEffect(() => {
        if (!open || actionType !== 'Edit' || !editData?.id) return;
        fetchTimerMetadata(editData.id);
    }, [open, actionType, editData?.id, fetchTimerMetadata]);

    useEffect(() => {
        if (!open || actionType !== 'Edit' || !editData?.id || !timerEnabled) return;
        fetchTimerMetadata(editData.id);
    }, [timerEnabled, open, actionType, editData?.id, fetchTimerMetadata]);

    useEffect(() => {
        if (!open || actionType !== 'Edit' || !editData?.id || !timerEnabled) return;
        fetchTimerMetadata(editData.id);
    }, [doneTagging, open, actionType, editData?.id, timerEnabled, fetchTimerMetadata]);

    const getTeamList = () => GameService.getAllTeams().then((res) => setTeamList(res));
    const getPlayerList = () => GameService.getAllPlayers().then((res) => setPlayerList(res));
    const handleClickPlayerOpen = () => setPlayerOpen(true);
    const updatePlayerListCallBack = () => getPlayerList();
    const handleClickTeamOpen = () => setTeamOpen(true);
    const homeTeamCallBack = useCallback((param) => setHomeTeam(param), []);
    const awayTeamCallBack = useCallback((param) => setAwayTeam(param), []);

    useEffect(() => {
        getTeamList();
        getPlayerList();
    }, []);

    const handleClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setAlertOpen(false);
    };

    const OpenAlert = (msg, type = 'success') => {
        setAlertOpen(true);
        setAlert(msg);
        setAlertType(type);
    };

    const buildTimerPayload = useCallback(() => {
        if (!timerEnabled) {
            return { enabled: false };
        }

        const parsedFontSize = parseInt(timerFontSize, 10);
        const overtimeValue = (overtimeStart || '').trim();
        const normalizedFirstHalfStart = hasFirstHalfStartTag ? (firstHalfStart || '').trim() : '';
        const normalizedSecondHalfStart = hasSecondHalfStartTag ? (secondHalfStart || '').trim() : '';
        const toRelativeOrNull = (url) => {
            const trimmed = (url || '').trim();
            if (!trimmed) return null;
            const relative = stripS3Base(trimmed);
            return relative || trimmed;
        };

        const normalizedLogos = {
            home: toRelativeOrNull(timerLogos.home),
            away: toRelativeOrNull(timerLogos.away)
        };

        const baseConfig = {
            enabled: true,
            logos: normalizedLogos,
            config: {
                fontName: timerFontName.trim() || defaultTimerConfig.fontName,
                fontSize: Number.isFinite(parsedFontSize) && parsedFontSize > 0 ? parsedFontSize : parseInt(defaultTimerConfig.fontSize, 10),
                fontColor: timerFontColor.trim() || defaultTimerConfig.fontColor,
                useBox: timerUseBox,
                boxColor: timerBoxColor.trim() || defaultTimerConfig.boxColor,
                boxAlpha: timerBoxAlpha,
                position: timerPosition,
                firstHalfStart: normalizedFirstHalfStart || null,
                secondHalfStart: normalizedSecondHalfStart || null,
                overtimeStart: overtimeValue.length ? overtimeValue : null,
                downscale: timerDownscale,
                encoder: timerEncoder
            }
        };

        if (timerMetadata?.logos) {
            baseConfig.config.logos = timerMetadata.logos;
        } else if (timerLogos.home || timerLogos.away) {
            baseConfig.config.logos = {
                home: timerLogos.home ? { image: timerLogos.home } : null,
                away: timerLogos.away ? { image: timerLogos.away } : null
            };
        }

        if (timerMetadata?.goalDetails) {
            baseConfig.config.goalDetails = timerMetadata.goalDetails;
        }

        if (timerMetadata?.scoreSummary) {
            baseConfig.config.goalSummary = timerMetadata.scoreSummary;
        }

        return baseConfig;
    }, [
        timerEnabled,
        stripS3Base,
        timerFontName,
        timerFontSize,
        timerFontColor,
        timerUseBox,
        timerBoxColor,
        timerBoxAlpha,
        timerPosition,
        firstHalfStart,
        secondHalfStart,
        hasFirstHalfStartTag,
        hasSecondHalfStartTag,
        overtimeStart,
        timerDownscale,
        timerEncoder,
        timerLogos,
        timerMetadata
    ]);

    const gameClicked = () => {
        if (!homeTeam || !awayTeam || !season || !league || !videoUrl?.length) {
            OpenAlert('Input enough data to add a new game!', 'warning');
            return;
        }
        setLoading(true);

        if (actionType === 'Add') {
            GameService.addGame({
                image,
                season_id: season.id,
                league_id: league.id,
                home_team_id: homeTeam.id,
                away_team_standing_id: 13,
                home_team_standing_id: 13,
                away_team_id: awayTeam.id,
                date: gameDate,
                video_url: videoUrl,
                mobile_video_url: mobileVideoUrl ? mobileVideoUrl : videoUrl,
                mute_video: muteVideo,
                done_tagging: doneTagging,
                timerPayload: buildTimerPayload()
            })
                .then((res) => {
                    gameListUpdated();
                    OpenAlert(t('addedGame'));
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        } else if (actionType === 'Edit') {
            GameService.updateGame({
                id: editData.id,
                image,
                season_id: season.id,
                league_id: league.id,
                home_team_id: homeTeam.id,
                away_team_id: awayTeam.id,
                date: gameDate,
                video_url: videoUrl,
                mobile_video_url: mobileVideoUrl ? mobileVideoUrl : videoUrl,
                mute_video: muteVideo,
                done_tagging: doneTagging,
                timerPayload: buildTimerPayload()
            })
                .then((res) => {
                    gameListUpdated();
                    OpenAlert(t('editedGame'));
                    setLoading(false);
                })
                .catch(() => {
                    setLoading(false);
                });
        }
    };

    const handleLeagueClose = (result) => {
        setLeagueOpen(false);

        if (!result) return;
        GameService.addLeague({ name: leagueName }).then(
            (response) => {
                setCount(count + 1);
                OpenAlert(`${leagueName} ${t('successAdd')}`);
            },
            (error) => { }
        );
    };

    useEffect(() => {
        (async () => {
            if (actionType === 'Edit' && doneTagging) {
                const text = `Game ${homeTeam.name} vs ${awayTeam.name} from ${getFormattedDate(gameDate)} is now tagged and ready for watching.`;
                const link = `https://analysis.geniusballsports.com/new_coach/games/${btoa(editData.id)}`;

                await GameService.getAllCoachesByTeam(season.id, league.id, homeTeam.id).then((res) => {
                    res.map(async (item) => {
                        await GameService.sendEmailToUser({ id: item.id, email: item.email, text: text, link: link });
                    });
                });
                await GameService.getAllCoachesByTeam(season.id, league.id, awayTeam.id).then((res) => {
                    res.map(async (item) => {
                        await GameService.sendEmailToUser({ id: item.id, email: item.email, text: text, link: link });
                    });
                });
            }
        })();
    }, [doneTagging]);

    return (
        <Dialog
            open={open}
            PaperProps={{
                sx: { minWidth: '90%' }
            }}
            onClose={() => setOpen(false)}
            scroll="paper"
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
        >
            {loading && (
                <div style={styles.loader}>
                    <CircularProgress />
                </div>
            )}
            <DialogTitle id="scroll-dialog-title">
                {actionType === 'Add' ? t('Add') : t('Edit')} {t('Game')}
            </DialogTitle>
            <DialogContent dividers={true} style={{ height: '90vh' }} ref={descriptionElementRef}>
                <Snackbar open={alertOpen} autoHideDuration={2000} onClose={handleClose} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                    <Alert onClose={handleClose} severity={alertType} sx={{ width: '100%' }}>
                        {alert}
                    </Alert>
                </Snackbar>
                <TeamFormDialog open={teamOpen} onResult={(res) => setTeamOpen(false)} t={t} />
                <PlayerFormDialog
                    open={playerOpen}
                    onResult={(res) => {
                        setPlayerOpen(res.open);
                        if (!res?.msg) {
                            OpenAlert(res.msg, res.result);
                        }
                        if (res?.status === 'success') {
                            setPlayerOpen(false);
                            getPlayerList();
                        }
                    }}
                    t={t}
                    positions={positions}
                    ageGroups={ageGroups}
                    roles={roles}
                />
                <Dialog open={leagueOpen} onClose={(e) => handleLeagueClose(false)}>
                    <DialogTitle>{t('newLeagueTitle')}</DialogTitle>
                    <DialogContent >
                        <DialogContentText>{t('newLeagueText')}</DialogContentText>
                        <TextField autoFocus margin="dense" id="name" value={leagueName} onChange={(e) => setLeagueName(e.target.value)} label="League Name" fullWidth variant="standard" />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={(e) => handleLeagueClose(false)}>{t('Cancel')}</Button>
                        <Button onClick={(e) => handleLeagueClose(true)}>{t('Add')}</Button>
                    </DialogActions>
                </Dialog>

                <Grid style={{ height: '70vh' }} container columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                    <Grid item xs={4}>
                        <Search
                            teamtype="home"
                            selectedTeamCallBack={homeTeamCallBack}
                            season={season}
                            league={league}
                            teamList={teamList}
                            playerList={playerList}
                            defaultTeamId={editData?.home_team_id}
                            updatePlayerListCallBack={updatePlayerListCallBack}
                            t={t}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', margin: 2 }}>
                            <div style={{ alignItems: 'center', gap: '10px', margin: 2 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 2 }}>
                                    <Button sx={{ width: 120 }} variant="outlined" onClick={() => handleClickTeamOpen()} startIcon={<AddIcon />}>
                                        {t('Team')}
                                    </Button>
                                    <Button sx={{ width: 120 }} variant="outlined" onClick={(e) => handleClickPlayerOpen()} startIcon={<AddIcon />}>
                                        {t('Player')}
                                    </Button>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 2 }}>
                                    <FormControlLabel
                                        sx={{ mt: 1 }}
                                        control={<Switch checked={muteVideo} onChange={() => setMuteVideo(!muteVideo)} inputProps={{ 'aria-label': 'controlled' }} />}
                                        label={`${t('Mute Video')}`}
                                    />
                                    <FormControlLabel
                                        sx={{ mt: 1 }}
                                        control={<Switch checked={doneTagging} onChange={handleDoneTagging} inputProps={{ 'aria-label': 'controlled' }} />}
                                        label={`${t('Done Tagging')}`}
                                    />
                                    <FormControlLabel
                                        sx={{ mt: 1 }}
                                        control={<Switch checked={timerEnabled} onChange={() => setTimerEnabled(!timerEnabled)} inputProps={{ 'aria-label': 'controlled' }} />}
                                        label={'Timer Overlay'}
                                    />
                                </div>
                            </div>
                            <div style={{ alignItems: 'center', gap: '10px', margin: 2 }}>
                                <Upload
                                    dirName={process.env.REACT_APP_DIR_GAME}
                                    onURL={(url) => setImage(url)}
                                    defaultImg={editData?.image?.length > 0 ? editData?.image : TEAM_ICON_DEFAULT}
                                    btn_name={t('Upload')}
                                />
                            </div>
                        </div>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={t('gameDate')}
                                value={gameDate}
                                onChange={(newValue) => {
                                    setGameDate(newValue);
                                }}
                                renderInput={(params) => <TextField {...params} fullWidth />}
                            />
                        </LocalizationProvider>
                        <Autocomplete
                            style={{ marginTop: 10 }}
                            id="combo-box-demo"
                            options={seasonList}
                            value={season}
                            isOptionEqualToValue={(option, value) => option && option.name}
                            getOptionLabel={(option) => (!option.name ? '' : option.name)}
                            renderOption={(props, option) => {
                                return (
                                    <li {...props} key={option.id}>
                                        {option.name}
                                    </li>
                                );
                            }}
                            fullWidth
                            renderInput={(params) => <TextField {...params} label={t('Season')} sx={{ my: 1 }} />}
                            onChange={(event, newValue) => {
                                setSeason(newValue);
                            }}
                        />
                        <div style={{ display: 'flex', width: '100%' }}>
                            <Autocomplete
                                id="combo-box-demo"
                                options={leagueList}
                                value={league}
                                isOptionEqualToValue={(option, value) => option && option.name}
                                getOptionLabel={(option) => (!option.name ? '' : option.name)}
                                fullWidth
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props} key={option.id}>
                                            {option.name}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => <TextField {...params} label={t('League')} sx={{ my: 1 }} />}
                                onChange={(event, newValue) => {
                                    setLeague(newValue);
                                }}
                            />
                            <IconButton style={{ alignSelf: 'center' }} aria-label="delete" size="large" onClick={(e) => setLeagueOpen(true)}>
                                <AddCircleIcon />
                            </IconButton>
                        </div>
                        <TextField
                            id="outlined-textarea"
                            label={t('VideoURL')}
                            placeholder={t('VideoURL')}
                            multiline
                            fullWidth
                            sx={{ my: 1 }}
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                        />
                        <TextField
                            id="outlined-textarea"
                            label={t('MobileVideoURL')}
                            placeholder={t('MobileVideoURL')}
                            multiline
                            fullWidth
                            sx={{ my: 1 }}
                            value={mobileVideoUrl}
                            onChange={(e) => setMobileVideoUrl(e.target.value)}
                        />
                        {actionType === 'Edit' && previousVideoUrl?.length > 0 && (
                            <TextField
                                id="outlined-textarea-previous"
                                label={t('PreviousVideoURL')}
                                placeholder={t('PreviousVideoURL')}
                                multiline
                                fullWidth
                                sx={{ my: 1 }}
                                value={previousVideoUrl}
                                InputProps={{ readOnly: true }}
                            />
                        )}
                        {timerEnabled && (
                            <>
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Timer Overlay Settings
                                    </Typography>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <Typography variant="subtitle1" gutterBottom>
                                                Team Logos
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2">Home Team</Typography>
                                                {timerLogos.home ? (
                                                    <Box component="img" src={timerLogos.home} alt="Home team logo" sx={{ maxHeight: 80, objectFit: 'contain' }} />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        No logo available yet.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2">Away Team</Typography>
                                                {timerLogos.away ? (
                                                    <Box component="img" src={timerLogos.away} alt="Away team logo" sx={{ maxHeight: 80, objectFit: 'contain' }} />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        No logo available yet.
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Grid>
                                        {timerMetadataWarning && (
                                            <Grid item xs={12}>
                                                <Alert severity="warning">{timerMetadataWarning}</Alert>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel id="timer-position-label">Clock Position</InputLabel>
                                                <Select
                                                    labelId="timer-position-label"
                                                    value={timerPosition}
                                                    label="Clock Position"
                                                    onChange={(e) => setTimerPosition(e.target.value)}
                                                >
                                                    {TIMER_POSITIONS.map((pos) => (
                                                        <MenuItem key={pos} value={pos}>
                                                            {pos.replace('-', ' ')}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Font Name"
                                                fullWidth
                                                value={timerFontName}
                                                onChange={(e) => setTimerFontName(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Font Size"
                                                type="number"
                                                fullWidth
                                                value={timerFontSize}
                                                onChange={(e) => setTimerFontSize(e.target.value)}
                                                inputProps={{ min: 10 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Font Color"
                                                placeholder="white or #FFFFFF"
                                                fullWidth
                                                value={timerFontColor}
                                                onChange={(e) => setTimerFontColor(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Box Color"
                                                placeholder="#000000"
                                                fullWidth
                                                value={timerBoxColor}
                                                onChange={(e) => setTimerBoxColor(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={<Switch checked={timerUseBox} onChange={() => setTimerUseBox(!timerUseBox)} />}
                                                label="Background Box"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="body2" gutterBottom>
                                                Box Transparency ({timerBoxAlpha}%)
                                            </Typography>
                                            <Slider
                                                value={timerBoxAlpha}
                                                onChange={(_, value) => setTimerBoxAlpha(Array.isArray(value) ? value[0] : value)}
                                                valueLabelDisplay="auto"
                                                min={0}
                                                max={100}
                                                disabled={!timerUseBox}
                                            />
                                        </Grid>
                                        {timerStartTimesError && (
                                            <Grid item xs={12}>
                                                <Alert severity="error">{timerStartTimesError}</Alert>
                                            </Grid>
                                        )}
                                        {!timerStartTimesLoading && (!hasFirstHalfStartTag || !hasSecondHalfStartTag) && (
                                            <Grid item xs={12}>
                                                <Alert severity="info">
                                                    Timer start times will auto-fill from tagging data. The timer won't be queued until both halves are tagged.
                                                </Alert>
                                            </Grid>
                                        )}
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                label="1st Half Start (from tags)"
                                                fullWidth
                                                value={firstHalfStart || ''}
                                                InputProps={{ readOnly: true }}
                                                helperText={timerStartTimesLoading ? 'Fetching period 1 start time…' : hasFirstHalfStartTag ? 'Derived from the first period 1 team tag.' : 'No period 1 tag detected yet.'}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                label="2nd Half Start (from tags)"
                                                fullWidth
                                                value={secondHalfStart || ''}
                                                InputProps={{ readOnly: true }}
                                                helperText={timerStartTimesLoading ? 'Fetching period 2 start time…' : hasSecondHalfStartTag ? 'Derived from the first period 2 team tag.' : 'No period 2 tag detected yet.'}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                label="Overtime Start (optional)"
                                                fullWidth
                                                value={overtimeStart}
                                                onChange={(e) => setOvertimeStart(e.target.value)}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormControl fullWidth>
                                                <InputLabel id="timer-encoder-label">Encoder Preference</InputLabel>
                                                <Select
                                                    labelId="timer-encoder-label"
                                                    value={timerEncoder}
                                                    label="Encoder Preference"
                                                    onChange={(e) => setTimerEncoder(e.target.value)}
                                                >
                                                    {ENCODER_OPTIONS.map((option) => (
                                                        <MenuItem key={option} value={option}>
                                                            {option}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                            <FormControlLabel
                                                control={<Switch checked={timerDownscale} onChange={() => setTimerDownscale(!timerDownscale)} />}
                                                label="Downscale to 1080p"
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </>
                        )}
                    </Grid>
                    <Grid item xs={4}>
                        <Search
                            teamtype="away"
                            selectedTeamCallBack={awayTeamCallBack}
                            season={season}
                            league={league}
                            teamList={teamList}
                            playerList={playerList}
                            defaultTeamId={editData?.away_team_id}
                            updatePlayerListCallBack={updatePlayerListCallBack}
                            t={t}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>{t('Close')}</Button>
                <Button variant="outlined" onClick={gameClicked}>
                    {actionType === 'Add' ? t('Add') : t('Edit')} {t('Game')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
